import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router';
import type { ModuleSlug } from '@/styles/tokens';
import { useAuth } from '@/auth/AuthContext';
import { canAccessAppraisals } from '@/auth/visibility';
import { ModuleIcon } from '@/shared/icons';
import { useModuleRegistries } from '@/shared/modules/store';
import { useWorkspaceName } from '@/shared/company';
import { resolveActiveSubNav, type NavItem } from './moduleNav';
import type { ModuleDef } from './modules';
import { isNavItemVisible } from './navVisibility';
import { useNavBadges } from './useNavBadges';
import { QuotincNavEntry } from '@/features/quotinc/QuotincNavEntry';

// Shared with ModuleRail on purpose: one "narrow rail" preference per browser,
// whichever interface the person is on.
const RAIL_COLLAPSED_KEY = 'hub-react-rail-collapsed';

interface Props {
  /** The module the route belongs to; null on the Hub home. */
  module: ModuleSlug | null;
  pathname: string;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onOpenSearch: () => void;
}

/**
 * The navigation of the Quotinc-family interface: ONE sidebar, in the same
 * style as Quotinc's (shared/lib/skin.ts, docs/quotinc-skin.md).
 *
 * It replaces the Dock + ModuleRail pair. Top to bottom, as in Quotinc:
 * the masthead (the Amassa mark, back to the Hub), a module switcher, the
 * current module's own pages as printed rows under small section labels,
 * and the person at the foot (with the Quotinc entry above it while the
 * install is linked to a Quotinc company). On the Hub home, where no module is open, the
 * rows are the modules themselves, so the rail is never empty.
 *
 * Collapsed, it narrows to the mark and one icon tile per module (the
 * module pages have no icons of their own to stand in for their labels).
 *
 * The data is the same as the original rail's: `MODULE_NAV` (the tenant's), the same
 * visibility predicate, the same attention counts. Only the drawing differs.
 */
export function HouseSidebar({ module, pathname, theme, onToggleTheme, onOpenSearch }: Props) {
  const { canSeeTile, user, logout } = useAuth();
  // The tenant's tree (Admin ▸ Modules): switched-off modules and pages are
  // already gone from these, and the labels are the tenant's.
  const { switcher, labels, nav } = useModuleRegistries();

  const modules = switcher.filter((m) =>
    m.appraisalsAware ? canSeeTile(m.tile) || canAccessAppraisals(user) : canSeeTile(m.tile),
  );
  const moduleTo = (m: ModuleDef) =>
    m.appraisalsAware && !canSeeTile(m.tile) && m.nonAdminTo ? m.nonAdminTo : m.to;

  // The workspace's name under the mark, once the company is named (Admin ▸
  // Company, or the install's build args). Asian Trails' install names no
  // company there: its sidebar is unchanged.
  const workspace = useWorkspaceName();

  const label = module ? (labels[module] ?? module) : 'Hub';
  const activeTo = module ? resolveActiveSubNav(module, pathname) : null;

  const badges = useNavBadges(module === 'coportal');
  const badgeFor = (to: string): number => {
    if (to === '/coportal/modifications') return badges.modifications;
    if (to === '/coportal/no-show') return badges.noShow;
    if (to === '/coportal/vehicles') return badges.vehicles;
    return 0;
  };

  const groups = module
    ? (nav[module] ?? [])
        .map((g) => ({
          ...g,
          items: g.items.filter((item: NavItem) => isNavItemVisible(module, item, user, canSeeTile)),
        }))
        .filter((g) => g.items.length > 0)
    : [];

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(RAIL_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* private window: the toggle still works for this session */
    }
  }, [collapsed]);

  // The module switcher popover.
  const [open, setOpen] = useState(false);
  const switchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!switchRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.fullName || user?.email || 'Account';
  const initials = (user?.fullName || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  return (
    <nav
      aria-label="Navigation"
      className={collapsed ? 'hnav hnav--collapsed' : 'hnav'}
      data-mod={module ?? undefined}
    >
      <button
        type="button"
        className="hnav__tab"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
          <path
            d={collapsed ? 'M3.5 2 L6.5 5 L3.5 8' : 'M6.5 2 L3.5 5 L6.5 8'}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <header className="hnav__mast">
        <NavLink to="/dashboard" className="hnav__home" aria-label="Amassa, Hub home" data-tour="home">
          {/* The logo file itself (v3, 2026-10-01): sienna, the knot cut
              out, cobalt dot. Exact proportions, no reconstruction. */}
          <img
            src="/amassa-logo-v3.png"
            alt=""
            className="hnav__logo"
            width={collapsed ? 38 : 52}
            height={collapsed ? 38 : 52}
          />
          {!collapsed && <span className="hnav__word">Amassa</span>}
          {!collapsed && workspace && <span className="hnav__ws">{workspace}</span>}
        </NavLink>

        {!collapsed && (
          <div className="hnav__switch" ref={switchRef} data-tour="switcher">
            <button
              type="button"
              className="hnav__switch-btn"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <span className="hnav__ink" aria-hidden />
              <span className="hnav__switch-label">{label}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden className={open ? 'is-open' : undefined}>
                <path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {open && (
              <div className="hnav__menu" role="menu">
                <NavLink to="/dashboard" role="menuitem" className={`hnav__menu-item${module ? '' : ' is-active'}`}>
                  <span className="hnav__ink hnav__ink--hub" aria-hidden />
                  Hub
                </NavLink>
                {modules.map((m) => (
                  <NavLink
                    key={m.slug}
                    to={moduleTo(m)}
                    role="menuitem"
                    data-mod={m.slug}
                    className={`hnav__menu-item${module === m.slug ? ' is-active' : ''}`}
                  >
                    <span className="hnav__ink" aria-hidden />
                    {m.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      <div className="hnav__scroll" data-tour={module ? 'pages' : undefined}>
        {collapsed ? (
          <ol className="hnav__tiles">
            {modules.map((m) => (
              <li key={m.slug}>
                <NavLink
                  to={moduleTo(m)}
                  data-mod={m.slug}
                  className={`hnav__tile${module === m.slug ? ' is-active' : ''}`}
                  aria-label={m.label}
                  title={m.label}
                >
                  <ModuleIcon mod={m.slug} size={20} />
                </NavLink>
              </li>
            ))}
          </ol>
        ) : module ? (
          groups.map((group, gi) => (
            <section key={group.heading ?? `__g${gi}`} className="hnav__group">
              {group.heading && <h2 className="hnav__heading">{group.heading}</h2>}
              <ol>
                {group.items.map((item) => {
                  const isActive = activeTo === item.to;
                  const count = badgeFor(item.to);
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={false}
                        className={`hnav__row${isActive ? ' is-active' : ''}`}
                        aria-label={count > 0 ? `${item.label}, ${count} pending` : undefined}
                      >
                        <span className="hnav__label">{item.label}</span>
                        {item.subtitle && <span className="hnav__sub">{item.subtitle}</span>}
                        {count > 0 && (
                          <span className="hnav__count" aria-hidden>
                            {count > 99 ? '99+' : count}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))
        ) : (
          <section className="hnav__group" data-tour="modules">
            <h2 className="hnav__heading">Modules</h2>
            <ol>
              {modules.map((m) => (
                <li key={m.slug}>
                  <NavLink to={moduleTo(m)} data-mod={m.slug} className="hnav__row" data-tour={`module:${m.slug}`}>
                    <span className="hnav__ink" aria-hidden />
                    <span className="hnav__label">{m.label}</span>
                  </NavLink>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      <footer className="hnav__foot" data-tour="me">
        {/* Quotinc, the sibling app: only while this install is linked. */}
        <QuotincNavEntry collapsed={collapsed} />
        <div className="hnav__who" title={name}>
          <span className="hnav__face" aria-hidden>
            {initials || '·'}
          </span>
          {!collapsed && (
            <span className="hnav__who-text">
              <span className="hnav__who-name">{name}</span>
              {user?.role && <span className="hnav__who-role">{user.role}</span>}
            </span>
          )}
        </div>
        <div className="hnav__utils">
          <button type="button" className="hnav__util" onClick={onOpenSearch} aria-label="Open the command bar" title="Search · ⌘K">
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
              <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10.5 10.5 L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            className="hnav__util"
            onClick={onToggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            title={theme === 'light' ? 'Dark theme' : 'Light theme'}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
              {theme === 'light' ? (
                <path d="M13 9.5 A5.5 5.5 0 1 1 6.5 3 A4.4 4.4 0 0 0 13 9.5 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              ) : (
                <>
                  <circle cx="8" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 1.5 V3 M8 13 V14.5 M1.5 8 H3 M13 8 H14.5 M3.4 3.4 L4.5 4.5 M11.5 11.5 L12.6 12.6 M3.4 12.6 L4.5 11.5 M11.5 4.5 L12.6 3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
          <button type="button" className="hnav__util" onClick={() => void logout()} aria-label="Sign out" title="Sign out">
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden>
              <path d="M6.5 2.5 H3 V13.5 H6.5 M10 5 L13 8 L10 11 M13 8 H6.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </footer>
    </nav>
  );
}
